/**
 * Ad Banner Manager — Super Admin
 * Path: /admin/ad-banners
 *
 * Lets admins create/edit/delete home-page promotional banners with
 * geo targeting (district / LSGD / ward / health-zone) and date range.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError, API_BASE } from "../../lib/api";
import {
  Plus, Trash, PencilSimple, Eye, EyeSlash, X, UploadSimple,
  MegaphoneSimple, MapPin, Calendar, CursorClick, ImagesSquare,
  ArrowsClockwise, CaretDown,
} from "@phosphor-icons/react";

const EMPTY_FORM = () => ({
  title: "",
  subtitle: "",
  images: [{ url: "", alt: "", link_url: "" }],
  link_url: "",
  targets: { district_ids: [], lsgd_ids: [], ward_ids: [], health_zone_ids: [] },
  start_date: new Date().toISOString().slice(0, 10),   // Default to today
  end_date: "",
  active: true,
  order: 0,
  layout: "single",
});

export default function AdBannerManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalFor, setModalFor] = useState(null); // "new" | banner object
  const [districts, setDistricts] = useState([]);
  const [zones, setZones] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/ads");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/locations?type=district").then(r => setDistricts(r.data || [])).catch(() => {});
    api.get("/zones").then(r => setZones(r.data || [])).catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try { await api.delete(`/crm/ads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (id) => {
    try { await api.post(`/crm/ads/${id}/toggle`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const kpi = useMemo(() => ({
    total: rows.length,
    active: rows.filter(r => r.active).length,
    clicks: rows.reduce((s, r) => s + (r.click_count || 0), 0),
  }), [rows]);

  return (
    <AppShell
      title="Ad Banner Manager"
      subtitle="Home-page promotions with geo & date targeting"
      actions={
        <div className="flex gap-2">
          <button onClick={load} data-testid="ads-refresh"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
            <ArrowsClockwise size={12} weight="bold"/> Refresh
          </button>
          <button onClick={() => setModalFor("new")} data-testid="ads-new"
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-semibold">
            <Plus size={12} weight="bold"/> New banner
          </button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="ads-manager">
        <div className="grid grid-cols-3 gap-4">
          <Kpi icon={MegaphoneSimple} label="Total banners" value={kpi.total} tone="slate"/>
          <Kpi icon={Eye} label="Active" value={kpi.active} tone="green"/>
          <Kpi icon={CursorClick} label="Total clicks" value={kpi.clicks} tone="red"/>
        </div>

        {loading ? (
          <div className="py-14 text-center text-sm text-[#8A8780]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center border border-dashed border-[#E5E2D9] rounded-md bg-white">
            <MegaphoneSimple size={32} className="inline-block text-[#8A8780] mb-2" weight="duotone"/>
            <div className="text-sm text-[#5C5A55]">No banners yet — create your first one.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map(b => (
              <BannerCard key={b.id} b={b} districts={districts} zones={zones}
                          onEdit={() => setModalFor(b)}
                          onDelete={() => remove(b.id)}
                          onToggle={() => toggle(b.id)}/>
            ))}
          </div>
        )}
      </div>

      {modalFor && (
        <BannerFormModal
          districts={districts}
          zones={zones}
          initial={modalFor === "new" ? EMPTY_FORM() : normalizeForForm(modalFor)}
          isNew={modalFor === "new"}
          onClose={() => setModalFor(null)}
          onSaved={() => { setModalFor(null); load(); }}
        />
      )}
    </AppShell>
  );
}

const normalizeForForm = (b) => ({
  ...EMPTY_FORM(),
  ...b,
  targets: { ...EMPTY_FORM().targets, ...(b.targets || {}) },
  images: b.images && b.images.length ? b.images : [{ url: "", alt: "", link_url: "" }],
  start_date: b.start_date || "",
  end_date: b.end_date || "",
  order: b.order ?? 0,
  layout: b.layout || "single",
});

// ─── Banner card ─────────────────────────────────────────
const BannerCard = ({ b, districts, zones, onEdit, onDelete, onToggle }) => {
  const t = b.targets || {};
  const distNames = (t.district_ids || []).map(id => districts.find(d => d.id === id)?.name).filter(Boolean);
  const zoneNames = (t.health_zone_ids || []).map(id => zones.find(z => z.id === id)?.name).filter(Boolean);
  const hasGeo = (t.district_ids?.length || t.lsgd_ids?.length || t.ward_ids?.length || t.health_zone_ids?.length);

  // Run status — Live / Starts in / Ended / Off
  const today = new Date().toISOString().slice(0, 10);
  const status = (() => {
    if (!b.active) return { label: "Off", tone: "off" };
    if (b.start_date && today < b.start_date) {
      const days = Math.max(1, Math.round((new Date(b.start_date) - new Date(today)) / 86400000));
      return { label: days === 1 ? "Starts tomorrow" : `Starts in ${days} days`, tone: "amber" };
    }
    if (b.end_date && today > b.end_date) {
      const days = Math.max(1, Math.round((new Date(today) - new Date(b.end_date)) / 86400000));
      return { label: days === 1 ? "Ended yesterday" : `Ended ${days} days ago`, tone: "off" };
    }
    return { label: "Live now", tone: "live" };
  })();
  const statusStyle = {
    live:  "bg-[#EEF7EC] text-[#166534]",
    amber: "bg-[#FFF8E6] text-[#7A5A11]",
    off:   "bg-[#F4C7B9] text-[#7A2E18]",
  }[status.tone];

  return (
    <div className={`bg-white rounded-2xl border border-[#E5E2D9] overflow-hidden ${b.active ? "" : "opacity-60"}`}
         data-testid={`banner-card-${b.id}`}>
      {/* Preview strip */}
      <div className="relative h-32 bg-[#F7F6F2] overflow-hidden">
        {b.images?.[0]?.url ? (
          <img src={b.images[0].url} alt={b.images[0].alt || b.title}
               className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImagesSquare size={28} weight="duotone" className="text-[#8A8780]"/>
          </div>
        )}
        {b.images && b.images.length > 1 && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
            {b.images.length} images
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <button onClick={onToggle} data-testid={`banner-toggle-${b.id}`}
            className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] ${statusStyle}`}>
            {b.active ? <Eye size={10} weight="fill"/> : <EyeSlash size={10} weight="fill"/>}
            {status.label}
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="text-[13px] font-bold text-[#111827] leading-tight">{b.title}</div>
        {b.subtitle && <div className="text-[11px] text-[#5C5A55] mt-0.5 line-clamp-2">{b.subtitle}</div>}

        <div className="mt-3 space-y-1.5 text-[11px] text-[#5C5A55]">
          <div className="flex items-start gap-1.5">
            <Calendar size={11} weight="bold" className="mt-[3px] shrink-0"/>
            <span>
              {b.start_date || "Today"} → {b.end_date || "Forever"}
            </span>
          </div>
          <div className="flex items-start gap-1.5">
            <MapPin size={11} weight="bold" className="mt-[3px] shrink-0"/>
            <span className="min-w-0 flex-1">
              {!hasGeo ? <span className="text-[#8A8780]">All areas</span> : (
                <span className="line-clamp-2">
                  {distNames.length > 0 && `${distNames.length} district(s)`}
                  {t.lsgd_ids?.length ? ` · ${t.lsgd_ids.length} LSGD(s)` : ""}
                  {t.ward_ids?.length ? ` · ${t.ward_ids.length} ward(s)` : ""}
                  {zoneNames.length > 0 && ` · ${zoneNames.length} zone(s)`}
                </span>
              )}
            </span>
          </div>
          {b.link_url && (
            <div className="flex items-start gap-1.5 truncate">
              <CursorClick size={11} weight="bold" className="mt-[3px] shrink-0"/>
              <span className="truncate text-[#DC2626]">{b.link_url}</span>
            </div>
          )}
          <div className="flex items-start gap-1.5">
            <CursorClick size={11} weight="fill" className="mt-[3px] shrink-0 text-[#DC2626]"/>
            <span className="font-mono font-bold text-[#DC2626]">{b.click_count || 0} clicks</span>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <button onClick={onEdit} data-testid={`banner-edit-${b.id}`}
            className="flex-1 h-9 border border-[#E5E2D9] rounded-md text-xs font-semibold hover:bg-[#F7F6F2] inline-flex items-center justify-center gap-1">
            <PencilSimple size={12} weight="bold"/> Edit
          </button>
          <button onClick={onDelete} data-testid={`banner-delete-${b.id}`}
            className="h-9 px-3 border border-[#F4C7B9] text-[#DC2626] rounded-md text-xs font-semibold hover:bg-[#FEE2E2] inline-flex items-center gap-1">
            <Trash size={12} weight="bold"/>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Create/Edit modal ───────────────────────────────────
const BannerFormModal = ({ initial, isNew, onClose, onSaved, districts, zones }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(-1);
  const [lsgds, setLsgds] = useState([]);
  const [wards, setWards] = useState([]);
  const [openTargets, setOpenTargets] = useState(false);

  // Load LSGDs when districts chosen
  useEffect(() => {
    const ids = form.targets.district_ids;
    if (ids.length === 0) { setLsgds([]); return; }
    Promise.all(ids.map(id => api.get(`/locations?type=lsgd&parent_id=${id}`).catch(() => ({ data: [] }))))
      .then(rs => setLsgds(rs.flatMap(r => r.data || [])));
  }, [form.targets.district_ids]);
  // Load wards when LSGDs chosen
  useEffect(() => {
    const ids = form.targets.lsgd_ids;
    if (ids.length === 0) { setWards([]); return; }
    Promise.all(ids.map(id => api.get(`/locations?type=ward&parent_id=${id}`).catch(() => ({ data: [] }))))
      .then(rs => setWards(rs.flatMap(r => r.data || [])));
  }, [form.targets.lsgd_ids]);

  const patchTargets = (key, ids) => setForm(f => ({ ...f, targets: { ...f.targets, [key]: ids } }));

  const upload = async (idx, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10 MB");
    setUploading(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("rescigo:auth") ? JSON.parse(localStorage.getItem("rescigo:auth"))?.token : null;
      const res = await fetch(`${API_BASE}/admin/media/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const j = await res.json();
      setForm(f => {
        const imgs = [...f.images];
        imgs[idx] = { ...imgs[idx], url: j.url };
        return { ...f, images: imgs };
      });
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(-1); }
  };

  const addImage = () => setForm(f => ({ ...f, images: [...f.images, { url: "", alt: "", link_url: "" }] }));
  const rmImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  const patchImage = (idx, patch) => setForm(f => {
    const imgs = [...f.images];
    imgs[idx] = { ...imgs[idx], ...patch };
    return { ...f, images: imgs };
  });

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const validImages = form.images.filter(i => (i.url || "").trim());
    if (validImages.length === 0) return toast.error("Add at least one image");
    setSaving(true);
    try {
      const body = { ...form, images: validImages };
      if (isNew) await api.post("/crm/ads", body);
      else await api.put(`/crm/ads/${initial.id}`, body);
      toast.success(isNew ? "Banner created" : "Banner updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl overflow-hidden"
           data-testid="banner-form-modal">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E2D9] bg-[#FAFAF7]">
          <MegaphoneSimple size={18} weight="fill" className="text-[#DC2626]"/>
          <div className="font-heading text-lg flex-1">{isNew ? "New banner" : "Edit banner"}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white inline-flex items-center justify-center">
            <X size={16} weight="bold"/>
          </button>
        </div>
        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Basic */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                     data-testid="banner-title"
                     className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
            </Field>
            <Field label="Layout">
              <select value={form.layout} onChange={e => setForm(f => ({ ...f, layout: e.target.value }))}
                      data-testid="banner-layout"
                      className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full bg-white">
                <option value="single">Single banner</option>
                <option value="carousel">Multi-image carousel</option>
              </select>
            </Field>
          </div>
          <Field label="Subtitle (optional)">
            <input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                   className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
          </Field>
          <Field label="Fallback click-through URL">
            <input value={form.link_url} placeholder="https://... or /me/services/…"
                   onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
                   data-testid="banner-link"
                   className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
          </Field>

          {/* Images */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold mb-2 flex items-center justify-between">
              <span>Images ({form.images.length})</span>
              <button onClick={addImage} data-testid="banner-add-image"
                className="text-[11px] text-[#DC2626] font-bold inline-flex items-center gap-1">
                <Plus size={11} weight="bold"/> Add image
              </button>
            </div>
            <div className="space-y-3">
              {form.images.map((img, i) => (
                <div key={i} className="rounded-xl border border-[#E5E2D9] bg-[#FAFAF7] p-3 flex gap-3">
                  <div className="w-24 h-24 rounded-md bg-[#F7F6F2] overflow-hidden shrink-0 flex items-center justify-center">
                    {img.url ? (
                      <img src={img.url} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <ImagesSquare size={24} weight="duotone" className="text-[#8A8780]"/>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex gap-2">
                      <input
                        value={img.url} placeholder="Image URL (or upload →)"
                        onChange={e => patchImage(i, { url: e.target.value })}
                        data-testid={`banner-img-url-${i}`}
                        className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs w-full"/>
                      <label
                        className={`h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1 cursor-pointer hover:bg-white ${uploading === i ? "opacity-50 pointer-events-none" : ""}`}
                        data-testid={`banner-img-upload-${i}`}>
                        <UploadSimple size={11} weight="bold"/>
                        {uploading === i ? "…" : "Upload"}
                        <input type="file" accept="image/*" className="hidden"
                               onChange={e => upload(i, e.target.files?.[0])}/>
                      </label>
                    </div>
                    <input value={img.alt || ""} placeholder="Alt text"
                           onChange={e => patchImage(i, { alt: e.target.value })}
                           className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs w-full"/>
                    <input value={img.link_url || ""} placeholder="Per-image link (overrides fallback)"
                           onChange={e => patchImage(i, { link_url: e.target.value })}
                           className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs w-full"/>
                  </div>
                  <button onClick={() => rmImage(i)} data-testid={`banner-img-remove-${i}`}
                    disabled={form.images.length === 1}
                    className="h-9 w-9 rounded-md border border-[#F4C7B9] text-[#DC2626] hover:bg-[#FEE2E2] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center shrink-0">
                    <Trash size={12} weight="bold"/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Start date">
                <input type="date" value={form.start_date}
                       onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                       data-testid="banner-start"
                       className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
              </Field>
              <Field label="End date">
                <input type="date" value={form.end_date}
                       onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                       data-testid="banner-end"
                       className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
              </Field>
              <Field label="Order">
                <input type="number" value={form.order}
                       onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                       className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm w-full"/>
              </Field>
            </div>
            <div className="mt-1.5 text-[10px] text-[#8A8780] italic">
              Leave dates blank to run forever. Today is {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}.
            </div>
          </div>

          {/* Targeting */}
          <div className="rounded-xl border border-[#E5E2D9]">
            <button onClick={() => setOpenTargets(v => !v)} type="button"
              className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-[#FAFAF7]">
              <MapPin size={14} weight="fill" className="text-[#DC2626]"/>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold flex-1">
                Geo targeting — leave empty for all areas
              </div>
              <CaretDown size={12} weight="bold" className={openTargets ? "rotate-180 transition" : "transition"}/>
            </button>
            {openTargets && (
              <div className="px-4 pb-4 space-y-3 border-t border-[#E5E2D9]">
                <MultiSelect
                  label="Districts"
                  options={districts.map(d => ({ id: d.id, label: d.name }))}
                  values={form.targets.district_ids}
                  onChange={ids => patchTargets("district_ids", ids)}
                  testid="target-districts"
                />
                <MultiSelect
                  label={`LSGDs ${form.targets.district_ids.length === 0 ? "(pick districts first)" : ""}`}
                  options={lsgds.map(l => ({ id: l.id, label: l.name }))}
                  values={form.targets.lsgd_ids}
                  onChange={ids => patchTargets("lsgd_ids", ids)}
                  disabled={form.targets.district_ids.length === 0}
                  testid="target-lsgds"
                />
                <MultiSelect
                  label={`Wards ${form.targets.lsgd_ids.length === 0 ? "(pick LSGDs first)" : ""}`}
                  options={wards.map(w => ({ id: w.id, label: w.name }))}
                  values={form.targets.ward_ids}
                  onChange={ids => patchTargets("ward_ids", ids)}
                  disabled={form.targets.lsgd_ids.length === 0}
                  testid="target-wards"
                />
                <MultiSelect
                  label="Health Zones"
                  options={zones.map(z => ({ id: z.id, label: z.name }))}
                  values={form.targets.health_zone_ids}
                  onChange={ids => patchTargets("health_zone_ids", ids)}
                  testid="target-zones"
                />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.active}
                   onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                   data-testid="banner-active"/>
            <span>Publish / activate</span>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-[#E5E2D9] bg-[#FAFAF7] flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="banner-save"
            className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-semibold disabled:opacity-50">
            {saving ? "Saving…" : (isNew ? "Create banner" : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Small helpers ───────────────────────────────────────
const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">{label}</div>
    {children}
  </label>
);

const MultiSelect = ({ label, options, values, onChange, disabled, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = options.filter(o => (o.label || "").toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id) => {
    if (values.includes(id)) onChange(values.filter(v => v !== id));
    else onChange([...values, id]);
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">{label}</div>
      <button type="button" onClick={() => !disabled && setOpen(v => !v)} disabled={disabled}
              data-testid={testid}
              className="w-full min-h-10 px-3 py-1.5 border border-[#E5E2D9] rounded-md text-xs text-left bg-white flex items-center gap-1 flex-wrap disabled:opacity-50">
        {values.length === 0 ? (
          <span className="text-[#8A8780]">Any {label.toLowerCase()}</span>
        ) : (
          values.map(id => {
            const opt = options.find(o => o.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 bg-[#FEE2E2] text-[#7F1D1D] px-2 py-0.5 rounded font-semibold">
                {opt?.label || id}
                <span onClick={(e) => { e.stopPropagation(); toggle(id); }}
                  className="cursor-pointer hover:text-[#DC2626]"><X size={9} weight="bold"/></span>
              </span>
            );
          })
        )}
        <CaretDown size={10} className="ml-auto text-[#8A8780]" weight="bold"/>
      </button>
      {open && !disabled && (
        <div className="mt-1 border border-[#E5E2D9] rounded-md bg-white max-h-56 overflow-y-auto shadow-lg">
          <div className="p-2 border-b border-[#F0EEE7] sticky top-0 bg-white">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
                   className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-xs"/>
          </div>
          {filtered.length === 0 ? (
            <div className="py-6 text-center text-xs text-[#8A8780]">No matches</div>
          ) : filtered.map(o => {
            const checked = values.includes(o.id);
            return (
              <button key={o.id} type="button" onClick={() => toggle(o.id)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[#FEF2F2] ${checked ? "bg-[#FEE2E2]" : ""}`}>
                <input type="checkbox" checked={checked} readOnly/>
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    red: { bg: "bg-gradient-to-br from-[#FEF7F6] to-[#FDECEA]", ring: "border-[#F4C7B9]", ic: "text-[#DC2626]" },
    green: { bg: "bg-gradient-to-br from-[#EEF7EC] to-[#D4E0D1]", ring: "border-[#C6D4C2]", ic: "text-[#15803D]" },
    slate: { bg: "bg-white", ring: "border-[#E5E2D9]", ic: "text-[#5C5A55]" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-xl border ${t.ring} ${t.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm inline-flex items-center justify-center">
          <Icon size={20} weight="fill" className={t.ic}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold">{label}</div>
          <div className="font-heading text-2xl mt-0.5 text-[#111827]">{value}</div>
        </div>
      </div>
    </div>
  );
};
